import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { MatExpansionPanel } from '@angular/material/expansion'
import * as lodash from 'lodash'
import {
  Channel,
  untilDestroy,
  Destroyable,
  isEmptyTable,
  isSomething,
  ChannelNameTokens,
  Toaster,
  ActionFailed,
} from '@tokeet-frontend/tv3-platform'
import { EMPTY, of, Subject, timer } from 'rxjs'
import { ChannelConnectHelperService } from '../../channel-connect-helper.service'
import { select, Store } from '@ngrx/store'
import { selectConnectionsByPropertyId } from '@tv3/store/connection/connection.selectors'
import { ConnectionView } from '@tv3/store/connection/connection.view'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { concatMap, debounceTime, filter, switchMap, take, takeUntil, tap, toArray } from 'rxjs/operators'
import { ConnectionHelperService } from '../../../connection-helper.service'
import { PushRatesToApiConnectionPayload } from '@tv3/store/connection/connection.types'
import { MatSort } from '@angular/material/sort'
import {
  HoldEventForEmptyCalendarPush,
  LoadConnectionsByChannel,
  PushRatesToConnectionComplete,
  PushRatesToConnectionFailed,
} from '@tv3/store/connection/connection.actions'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { Actions, ofType } from '@ngrx/effects'
import { ConnectionService } from '@tv3/store/connection/connection.service'

// TV4-5903: the backend room refresh is async (`GET /bdc/refresh/rooms` schedules a job and
// returns immediately), so rooms are awaited by re-requesting them. Bound that loop: back off
// between requests and give up after the timeout instead of spinning forever.
const ROOMS_POLL_TIMEOUT = 2 * 60 * 1000
const ROOMS_POLL_MAX_DELAY = 5000

type RoomsStatus = 'idle' | 'polling' | 'loaded' | 'empty'

@Component({
  selector: 'app-connect-bdc-wizard-step4',
  templateUrl: './connect-bdc-wizard-step4.component.html',
  styleUrls: ['./connect-bdc-wizard-step4.component.scss'],
  providers: [ChannelConnectHelperService],
})
export class ConnectBDCWizardStep4Component extends Destroyable implements OnInit {
  // TV4-6111: shown on the inert "Link Rental" control of a '....' placeholder row
  placeholderRowTooltip = $localize`Booking.com has not returned any rooms for this property yet. Refresh the room list and link a real room once one appears.`

  @ViewChild('settings') startStep: MatExpansionPanel
  @Input() channel: Channel
  @Input() set propertyId(id: string) {
    this.propertyId$.next(id)
  }
  propertyId$ = new Subject<string>()

  @Output() prev = new EventEmitter()
  @Output() next = new EventEmitter()

  @ViewChild('paginator') set paginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator
  }
  @ViewChild(MatSort) set sort(sort: MatSort) {
    this.dataSource.sort = sort
  }
  get sort() {
    return this.dataSource.sort
  }

  displayedColumns: string[] = ['roomName', 'propertyId', 'roomId', 'rentalName', 'rateCategory']
  dataSource = new MatTableDataSource<ConnectionView>()
  isEmptyTable$ = isEmptyTable(this.dataSource)
  pushRateMap = new Map<string, PushRatesToApiConnectionPayload>()
  tmpHoldEventMap = new Map<string, string>()

  roomsStatus: RoomsStatus = 'idle'
  private pollAttempts = 0
  private stopPolling$ = new Subject<void>()

  get unlinkedCount(): number {
    return this.dataSource.data.filter((t) => !t.rental).length
  }

  // Linked rooms whose rates have never been pushed. booking.com holds a room at Pending
  // until it has an availability pull, an availability push AND a rate push, so these rooms
  // are as stuck as the unlinked ones -- and nothing used to say so (TV4-6051).
  get missingRateCount(): number {
    return this.dataSource.data.filter((t) => !!t.rental && !this.pushRateMap.has(t.roomId as string)).length
  }

  constructor(
    private store: Store<any>,
    private actions: Actions,
    private toaster: Toaster,
    private connectionService: ConnectionService,
    private connectionHelperService: ConnectionHelperService,
    private channelHelperService: ChannelConnectHelperService
  ) {
    super()
  }

  ngOnInit(): void {
    this.propertyId$
      .pipe(
        // switching to EMPTY on a cleared propertyId drops the previous property's selector,
        // so a reset() can't be undone by a later store emission repopulating the table
        switchMap((id) => {
          if (!isSomething(id)) {
            return EMPTY
          }
          this.startPolling()
          return this.store.pipe(select(selectConnectionsByPropertyId(id, true)))
        }),
        debounceTime(1000),
        untilDestroy(this)
      )
      .subscribe((items: ConnectionView[]) => {
        // TV4-6062: this step maps rentals to the property's CURRENT rooms. Orphan rows for
        // mappings whose room no longer exists on the channel do not belong here (linking one
        // would target a dead room id) — they are handled in Mapped Rentals.
        items = items.filter((t) => !t.roomMissing)
        const isLoaded = items.find((t) => !!t.roomId && !!lodash.trim(String(t.roomId), '.'))
        if (isLoaded) {
          this.roomsStatus = 'loaded'
          this.dataSource.data = items
        }
      })

    // save temporary hold event id
    this.actions
      .pipe(
        ofType(HoldEventForEmptyCalendarPush),
        tap(({ connId, holdEvent }) => this.tmpHoldEventMap.set(connId, holdEvent.id)),
        untilDestroy(this)
      )
      .subscribe()
  }

  reset() {
    this.stopPolling$.next()
    this.roomsStatus = 'idle'
    this.propertyId = ''
    this.dataSource.data = []
  }

  onRetryRooms() {
    this.startPolling()
    // re-schedules the backend room-refresh job; deliberately NOT doAfterConnected, which
    // logs the add-channel Amplitude events and must fire once per actual connect
    this.channelHelperService.refreshRooms(this.channel)
  }

  private startPolling() {
    this.stopPolling$.next()
    this.roomsStatus = 'polling'
    this.pollAttempts = 0
    timer(ROOMS_POLL_TIMEOUT)
      .pipe(takeUntil(this.stopPolling$), untilDestroy(this))
      .subscribe(() => {
        if (this.roomsStatus === 'polling') {
          this.roomsStatus = 'empty'
        }
      })
    this.scheduleNextPoll()
  }

  // Timer-driven so a failed load request can't stall the loop. The load effect keeps requests
  // single-flight, and silent failures let later ticks retry without showing repeated toasts.
  // The chain ends when rooms arrive, the deadline fires, or polling is stopped.
  private scheduleNextPoll() {
    this.pollAttempts += 1
    timer(Math.min(this.pollAttempts * 1000, ROOMS_POLL_MAX_DELAY))
      .pipe(takeUntil(this.stopPolling$), untilDestroy(this))
      .subscribe(() => {
        if (this.roomsStatus !== 'polling') {
          return
        }
        this.store.dispatch(
          LoadConnectionsByChannel({ channelName: this.channel.name, channelId: this.channel.id, silent: true })
        )
        this.scheduleNextPoll()
      })
  }

  onPushRates() {
    const items = this.dataSource.data.filter((t) => !!t.rental)
    if (!items.length) {
      this.toaster.warning($localize`No linked properties found. Please link your property to rental to push rates.`)
      return
    }
    // TV4-6051: drive the push from the linked rows, not from pushRateMap. Looking a row up
    // by the map's roomId returned undefined once a room had been unlinked after its category
    // was picked, and reading channelId off it threw -- killing the concatMap, so every room
    // queued behind it was silently left unpushed too.
    const pushable = items.filter((t) => this.pushRateMap.has(t.roomId as string))
    const skipped = items.length - pushable.length

    if (!pushable.length) {
      this.toaster.warning($localize`No rates found. Please select rate category to push rates.`)
      return
    }

    of(...pushable)
      .pipe(
        concatMap((item) =>
          this.connectionService.pushRates(
            ChannelNameTokens.BookingAPI,
            item.channelId,
            this.pushRateMap.get(item.roomId as string)
          )
        ),
        toArray()
      )
      .subscribe(
        () => {
          // Reporting a flat success while quietly skipping rooms that have no category is
          // how rooms ended up Pending with nobody the wiser. Say what was left out.
          this.toaster.info(
            skipped
              ? $localize`Rate push scheduled for ${pushable.length} room(s). ${skipped} room(s) skipped: no rate category selected.`
              : $localize`Rate push scheduled.`
          )
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onImportBookings() {
    const items = this.dataSource.data.filter((t) => !!t.rental)
    if (!items.length) {
      this.toaster.warning(
        $localize`No linked properties found. Please link your property to rental to import bookings.`
      )
      return
    }

    this.connectionHelperService.importAllBookings(this.channel.name, items)
  }

  onPushAvailability() {
    const items = this.dataSource.data.filter((t) => !!t.rental)
    if (!items.length) {
      this.toaster.warning(
        $localize`No linked properties found. Please link your property to rental to push availability.`
      )
      return
    }

    this.connectionHelperService.pushAllAvailability(this.channel.name, items)
  }

  onNext() {
    this.next.emit()
  }

  onSearch(term: string) {
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  onRental(rentalId: string) {
    this.store.dispatch(OpenRentalOverlay({ rentalId }))
  }

  // TV4-6051: picking a category has to actually push. This used to call the payload-only
  // helper and stop at `pushRateMap.set` -- a plain in-memory Map -- so the dialog promised
  // "This will push your rental rates ... to the selected channel", the wizard's last step
  // promised the room would go Active "after the first successful availability and rate
  // sync", and no request was ever sent. Availability reached booking.com on its own (the
  // server chains a push behind the link-time bookings import, TV4-5906) but rates never
  // did, so the room sat at Pending until someone found Actions > Push Rates by hand.
  // Use the same helper every other channel wizard uses; the map now records what a room was
  // pushed with, which is what the Rate Category column reads.
  onSelectRateCategory(connection: ConnectionView) {
    this.connectionHelperService
      .pushRatesForAPIConnection(this.channel, connection.rental, connection)
      .subscribe((data: PushRatesToApiConnectionPayload) => {
        this.pushRateMap.set(connection.roomId as string, data)

        // The helper dispatches the push, it does not await it. Take the label back off if
        // this room's own push fails, so the row keeps its warning and invites a retry.
        // Correlated both ways: another room's result must not be read as this one's.
        this.actions
          .pipe(
            ofType(PushRatesToConnectionComplete, PushRatesToConnectionFailed),
            filter((action: any) =>
              action.type === PushRatesToConnectionFailed.type
                ? action.connId === connection.id
                : action.update?.id === connection.id
            ),
            take(1),
            untilDestroy(this)
          )
          .subscribe((action: any) => {
            if (action.type === PushRatesToConnectionFailed.type) {
              this.pushRateMap.delete(connection.roomId as string)
            }
          })
      })
  }

  onLink(connection: ConnectionView) {
    const help: string =
      $localize`Please link your existing AdvanceCM rental to the correct Booking.com room ID.` +
      '<br>' +
      $localize`Before linking, keep in mind:` +
      '<br>' +
      $localize`• A Booking.com property can include multiple room IDs.` +
      '<br>' +
      $localize`• Each room should have its own calendar and rates — not shared with other rooms under the same property.` +
      '<br>' +
      $localize`• If you see more than one room listed on the same calendar, please get in touch with your Booking.com account manager to separate them.`

    this.connectionHelperService.linkRental(this.channel.name, connection, help).subscribe()
  }
}
